import { useMemo } from 'react';
import { useT } from '../../../i18n/hooks';
import { dateDay } from '../../lib/format';
import { Button } from 'react-aria-components';
import { FM } from './messages';
import { Field } from './Field';
import { NumberField } from './NumberField';
import { Select } from './Select';
import { useCalendarLocale } from './locale';
import type { Choice, SeasonDay, SeasonDayPickerProps, SeasonRange, SeasonRangePickerProps } from './types';
import './styles';

/** Високосный опорный год: `02-29` — допустимая дата окна. */
const REF_YEAR = 2000;

const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

function parseMd(md: string | undefined): { month: number; day: number } | null {
  const match = /^(\d{2})-(\d{2})$/.exec(md ?? '');
  if (!match) return null;
  const month = Number(match[1]);
  const day = Number(match[2]);
  return month >= 1 && month <= 12 && day >= 1 && day <= 31 ? { month, day } : null;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** День сезона словами: «5 сен предыдущего года»; год окна дописывается, только если он не «тот же». */
function useDayText() {
  const t = useT();
  return (value: SeasonDay | null): string => {
    if (!value) return '—';
    const base = dateDay(`${REF_YEAR}-${value.md}`);
    if (value.yearShift === 0) return base;
    return t(FM.seasonWithYear, { date: base, year: value.yearShift < 0 ? t(FM.seasonPrev) : t(FM.seasonNext) });
  };
}

function DayEditor({ legend, value, onChange, allowYearShift, isDisabled, isInvalid, size }: { legend: string; value: SeasonDay | null; onChange: (value: SeasonDay | null) => void; allowYearShift: boolean; isDisabled?: boolean; isInvalid?: boolean; size?: 'sm' | 'md' }) {
  const t = useT();
  const locale = useCalendarLocale();
  const parsed = parseMd(value?.md);
  const months = useMemo<Choice[]>(() => {
    const format = new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC' });
    return Array.from({ length: 12 }, (_, index) => {
      const name = format.format(new Date(Date.UTC(REF_YEAR, index, 1)));
      return { id: pad(index + 1), name: name.charAt(0).toLocaleUpperCase(locale) + name.slice(1) };
    });
  }, [locale]);
  const years: Choice[] = [
    { id: '-1', name: t(FM.seasonPrev) },
    { id: '0', name: t(FM.seasonSame) },
    { id: '1', name: t(FM.seasonNext) },
  ];

  const emit = (month: number | null, day: number | null, yearShift: number) => {
    if (month === null && day === null) return onChange(null);
    const m = month ?? 1;
    const maxDay = DAYS_IN_MONTH[m - 1] ?? 31;
    onChange({ md: `${pad(m)}-${pad(Math.min(day ?? 1, maxDay))}`, yearShift });
  };

  return (
    <Field as="fieldset" label={legend} isInvalid={isInvalid} isDisabled={isDisabled} className="fm-season-end">
      <div className="fm-season-row">
        <NumberField className="fm-season-day" size={size} label={t(FM.seasonDay)} value={parsed?.day ?? null} minValue={1} maxValue={parsed ? (DAYS_IN_MONTH[parsed.month - 1] ?? 31) : 31} fractionDigits={0} isDisabled={isDisabled} isInvalid={isInvalid} onChange={(day) => emit(parsed?.month ?? null, day, value?.yearShift ?? 0)} />
        <Select className="fm-season-month" size={size} label={t(FM.seasonMonth)} value={parsed ? pad(parsed.month) : ''} options={months} isDisabled={isDisabled} isInvalid={isInvalid} onChange={(month) => emit(month === '' ? null : Number(month), parsed?.day ?? null, value?.yearShift ?? 0)} searchable={false} />
        {allowYearShift ? <Select className="fm-season-year" size={size} label={t(FM.seasonYear)} value={String(value?.yearShift ?? 0)} options={years} isDisabled={isDisabled || !value} isInvalid={isInvalid} onChange={(year) => value && onChange({ ...value, yearShift: Number(year || 0) })} searchable={false} /> : null}
      </div>
    </Field>
  );
}

/**
 * «День и месяц сезона»: окно шаблона без абсолютного года. Значение —
 * `{ md: 'ММ-ДД', yearShift: -1 | 0 | 1 }` на каждой границе: «5 сентября
 * предыдущего года» — это `{ md: '09-05', yearShift: -1 }`. Год урожая
 * подставляется при запуске агроцикла, а не в шаблоне.
 *
 * Календаря нет намеренно: в нём пришлось бы показывать год, которого у
 * шаблона нет. Число и месяц набираются, год окна выбирается из трёх.
 */
export function SeasonRangePicker({ label, 'aria-label': ariaLabel, explain, description, errorMessage, isInvalid, isRequired, isDisabled, size = 'md', className, value, onChange, allowYearShift = true, plan }: SeasonRangePickerProps) {
  const t = useT();
  const dayText = useDayText();
  const same = (a: SeasonDay | null, b: SeasonDay | null) => (a?.md ?? null) === (b?.md ?? null) && (a?.yearShift ?? 0) === (b?.yearShift ?? 0);
  const planDiffers = plan ? !same(plan.from, value.from) || !same(plan.to, value.to) : false;
  const set = (patch: Partial<SeasonRange>) => onChange({ ...value, ...patch });
  return (
    <Field as="fieldset" label={label} aria-label={ariaLabel} explain={explain} description={description} errorMessage={errorMessage} isInvalid={isInvalid} isRequired={isRequired} isDisabled={isDisabled} className={['fm-season', className].filter(Boolean).join(' ')}>
      <DayEditor legend={t(FM.seasonFrom)} value={value.from} onChange={(from) => set({ from })} allowYearShift={allowYearShift} isDisabled={isDisabled} isInvalid={isInvalid} size={size} />
      <DayEditor legend={t(FM.seasonTo)} value={value.to} onChange={(to) => set({ to })} allowYearShift={allowYearShift} isDisabled={isDisabled} isInvalid={isInvalid} size={size} />
      {plan ? (
        <div className="fm-plan">
          <span>{t(FM.seasonPlan, { from: dayText(plan.from), to: dayText(plan.to) })}</span>
          {planDiffers && !isDisabled ? (
            <Button className="fm-linkbtn" onPress={() => onChange(plan)}>
              {t(FM.rangeApplyPlan)}
            </Button>
          ) : null}
        </div>
      ) : null}
    </Field>
  );
}

/**
 * Один день сезона без года (число, месяц, к какому году относится): начало
 * работы, у которой конец задан длительностью. Тот же редактор, что внутри
 * `SeasonRangePicker`, поэтому и значение то же — `{ md, yearShift }`.
 */
export function SeasonDayPicker({ label, value, onChange, allowYearShift = true, isDisabled, isInvalid, size = 'md' }: SeasonDayPickerProps) {
  return <DayEditor legend={label} value={value} onChange={onChange} allowYearShift={allowYearShift} isDisabled={isDisabled} isInvalid={isInvalid} size={size} />;
}
